import { useEffect, useMemo, useRef, useState } from "react";
import { SiteTextsStore, type SiteTextEntry } from "@Front/Stores/SiteTextsStore";
import { normalizeSiteTextLocale } from "@Front/Stores/siteTextLocale";
import { getSiteTextFallbackDef } from "@Front/Stores/defaultSiteTextFallbacks";
import { runSiteTextsPreloadAttempt } from "@Front/Stores/siteTextsPreloadExecution";

export function useSiteTextsNormalizedLocale(localeProp: string): string {
	return useMemo(() => normalizeSiteTextLocale(localeProp), [localeProp]);
}

export function useSiteTextsNormalizedLists(scopes: string[], textKeys: string[]): {
	normalizedScopes: string[];
	normalizedTextKeys: string[];
} {
	const normalizedScopes = useMemo(
		() =>
			scopes
				.map((scope) => scope.trim())
				.filter(Boolean)
				.sort(),
		[scopes],
	);
	const normalizedTextKeys = useMemo(
		() =>
			textKeys
				.map((key) => key.trim())
				.filter(Boolean)
				.sort(),
		[textKeys],
	);
	return { normalizedScopes, normalizedTextKeys };
}

export function useSiteTextsPreloadCacheKey(locale: string, normalizedScopes: string[], normalizedTextKeys: string[]): string {
	return useMemo(() => {
		const normalizedScopesKey = normalizedScopes.join(",");
		const normalizedTextKeysKey = normalizedTextKeys.join(",");
		return `${locale}|${normalizedScopesKey}|${normalizedTextKeysKey}`;
	}, [locale, normalizedScopes, normalizedTextKeys]);
}

export function useSiteTextsPreloadEffect(
	locale: string,
	normalizedScopes: string[],
	normalizedTextKeys: string[],
	preloadCacheKey: string,
): { isLoading: boolean; error: Error | null } {
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<Error | null>(null);

	const localeRef = useRef(locale);
	const scopesRef = useRef(normalizedScopes);
	const textKeysRef = useRef(normalizedTextKeys);
	localeRef.current = locale;
	scopesRef.current = normalizedScopes;
	textKeysRef.current = normalizedTextKeys;

	useEffect(() => {
		let cancelled = false;
		const run = async (): Promise<void> => {
			const loc = localeRef.current;
			const scopes = scopesRef.current;
			const textKeys = textKeysRef.current;
			setIsLoading(true);
			setError(null);
			const outcome = await runSiteTextsPreloadAttempt(loc, scopes, textKeys);
			if (cancelled) {
				return;
			}
			if (outcome.success) {
				setIsLoading(false);
				return;
			}
			setError(outcome.error);
			setIsLoading(false);
		};
		void run();
		return (): void => {
			cancelled = true;
		};
	}, [preloadCacheKey]);

	return { isLoading, error };
}

type SiteTextsContextApi = {
	locale: string;
	getEntry: (textKey: string) => SiteTextEntry;
	getContent: (textKey: string) => string;
	has: (textKey: string) => boolean;
};

export function useSiteTextsContextApi(locale: string, isLoading: boolean, error: Error | null): SiteTextsContextApi {
	return useMemo<SiteTextsContextApi>(() => {
		const safeGetEntry = (textKey: string): SiteTextEntry => {
			if (isLoading || error) {
				if (SiteTextsStore.instance.has(textKey, locale)) {
					return SiteTextsStore.instance.get(textKey, locale);
				}
				const fallbackDef = getSiteTextFallbackDef(textKey, locale);
				const scopeResolved = fallbackDef?.scope ?? (textKey.split(".")[0] || "unknown");
				return {
					textKey,
					locale,
					scope: scopeResolved,
					content: fallbackDef?.content ?? "",
					isHtml: false,
					version: 0,
					updatedAt: null,
				};
			}
			return SiteTextsStore.instance.get(textKey, locale);
		};

		const safeGetContent = (textKey: string): string => safeGetEntry(textKey).content;
		const has = (textKey: string): boolean => SiteTextsStore.instance.has(textKey, locale);

		return {
			locale,
			getEntry: safeGetEntry,
			getContent: safeGetContent,
			has,
		};
	}, [locale, isLoading, error]);
}
